import { powerUpAtlas } from '../animation/Theme';
import { openMultiplayerSetup } from '../multiplayer/MultiplayerSetup';
import type { RunSettings } from '../multiplayer/RunSettings';
import { POWERUP_LEGEND } from '../ui/PowerUpLegend';
import { createModernMenu, openSoundSettings, type MenuAction } from '../ui/ModernMenu';
import Phaser from 'phaser';
import { GAME_W, GAME_H } from '../constants';
import { GameMode } from '../types';
import { saveSystem } from '../systems/SaveSystem';
import { audioSystem } from '../systems/AudioSystem';

export default class MainMenuScene extends Phaser.Scene {
  private buttonConfigs: MenuAction[] = [];
  private selectedIndex = 0;
  private menuElement!: HTMLElement;
  private setupClose: (() => void) | null = null;
  private helpPanel: Phaser.GameObjects.Container | null = null;
  private cursorKeys!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wasdKeys!: { up: Phaser.Input.Keyboard.Key; down: Phaser.Input.Keyboard.Key; enter: Phaser.Input.Keyboard.Key };
  private starting = false;
  private readonly audio = audioSystem;
  constructor() { super({ key: 'MainMenuScene' }); }
  create(): void {
    this.starting = false; this.selectedIndex = 0; this.setupClose = null; this.helpPanel = null;
    this.audio.masterVolume = saveSystem.settings.masterVolume;
    this.audio.musicVolume = saveSystem.settings.musicVolume;
    this.audio.sfxVolume = saveSystem.settings.sfxVolume;
    this.audio.startMusic('menu');
    this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x091c24);
    this.buttonConfigs = [
      { label: 'Campanie', color: 0xe0efbe, action: () => this.startGame(GameMode.CAMPAIGN) },
      { label: 'Arcade clasic', color: 0x88dabb, action: () => this.startGame(GameMode.ARCADE) },
      { label: 'Supraviețuire', color: 0xffcf82, action: () => this.startGame(GameMode.SURVIVAL) },
      { label: 'Multiplayer', color: 0x88dabb, action: () => {
        if (this.setupClose) return;
        this.setupClose = openMultiplayerSetup(this, settings => this.startGame(GameMode.MULTIPLAYER, settings), () => this.setupClose = null);
      } },
      { label: 'Setări', color: 0x88dabb, action: () => this.openSettings() },
      { label: 'Help', color: 0xffcf82, action: () => this.openHowToPlay() },
      { label: 'Test surprize', color: 0x88dabb, action: () => this.startGame(GameMode.CAMPAIGN, { testStage: true }) },
      { label: 'Multiplayer online', color: 0x88dabb, action: () => { this.audio.stopMusic(); this.scene.start('OnlineScene'); } },
    ];
    this.menuElement = createModernMenu(this, this.buttonConfigs, index => this.select(index));
    this.cursorKeys = this.input.keyboard!.createCursorKeys();
    this.wasdKeys = { up: this.input.keyboard!.addKey('W'), down: this.input.keyboard!.addKey('S'), enter: this.input.keyboard!.addKey('ENTER') };
    this.select(0);
    let onlineSession=false;try{onlineSession=Boolean(sessionStorage.getItem('blast-arena-online-session'));}catch{}
    if (onlineSession || new URL(location.href).searchParams.has('room')) this.buttonConfigs[7].action();
  }
  update(): void {
    if (this.helpPanel || this.setupClose || this.starting) return;
    // Native buttons keep their normal Tab/Enter behavior without double activation.
    if (this.menuElement.contains(document.activeElement) && document.activeElement?.tagName === 'BUTTON') return;
    if (Phaser.Input.Keyboard.JustDown(this.cursorKeys.up) || Phaser.Input.Keyboard.JustDown(this.wasdKeys.up)) this.select((this.selectedIndex + this.buttonConfigs.length - 1) % this.buttonConfigs.length);
    else if (Phaser.Input.Keyboard.JustDown(this.cursorKeys.down) || Phaser.Input.Keyboard.JustDown(this.wasdKeys.down)) this.select((this.selectedIndex + 1) % this.buttonConfigs.length);
    else if (Phaser.Input.Keyboard.JustDown(this.wasdKeys.enter) || Phaser.Input.Keyboard.JustDown(this.cursorKeys.space)) {
      this.audio.resume(); this.buttonConfigs[this.selectedIndex].action();
    }
  }
  private select(index: number): void {
    this.selectedIndex = index;
    for (const button of this.menuElement?.querySelectorAll<HTMLElement>('[data-menu-index]') ?? []) button.dataset.selected = String(Number(button.dataset.menuIndex) === index);
  }
  private startGame(mode: GameMode, settings: RunSettings = {}): void {
    if (this.starting) return; this.starting = true; this.audio.stopMusic();
    this.scene.start('GameScene', { level: 1, mode, score: 0, ...settings });
  }
  private openSettings(): void {
    if (this.setupClose) return;
    this.setupClose = openSoundSettings(this, () => this.setupClose = null);
  }
  private openHowToPlay(): void {
    if (this.helpPanel || this.setupClose) return;
    this.menuElement.hidden = true;
    const modal = this.add.container(0, 0).setDepth(50);
    this.helpPanel = modal;
    const overlay = this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0, 0.85).setInteractive();
    const width = 840, height = 740;
    const left = (GAME_W - width) / 2, top = (GAME_H - height) / 2;
    const panel = this.add.rectangle(GAME_W / 2, GAME_H / 2, width, height, 0x101c30)
      .setStrokeStyle(2, 0xffaa00).setInteractive();
    modal.add([overlay, panel]);
    modal.add(this.add.text(GAME_W / 2, top + 34, 'CUM SE JOACĂ • SURPRIZE', {
      fontFamily: 'Arial Black, Arial', fontSize: '28px', color: '#ffcc66',
    }).setOrigin(0.5));
    modal.add(this.add.text(left + 28, top + 72,
      'WASD / săgeți: mișcare   •   SPACE / ENTER: bombă   •   ESC: închide\n' +
      'Distruge lăzile și adună surprizele. Inamicii și exploziile îți iau o viață.\n' +
      'Elimină toți inamicii, apoi intră în portalul verde pentru nivelul următor.',
      { fontFamily: 'Arial', fontSize: '17px', color: '#cbd9ec', lineSpacing: 7 }));
    modal.add(this.add.text(left + 28, top + 162, 'Fiecare iconiță este aceeași ca în joc:', {
      fontFamily: 'Arial', fontSize: '17px', color: '#80e9dc',
    }));
    POWERUP_LEGEND.forEach((item, i) => {
      const x = left + 28 + Math.floor(i / 5) * 402;
      const y = top + 210 + (i % 5) * 90;
      modal.add(this.add.image(x + 25, y + 25, powerUpAtlas(item.type), `powerups/${item.type}`).setOrigin(0.5).setScale(0.55));
      modal.add(this.add.text(x + 62, y, item.name, {
        fontFamily: 'Arial', fontStyle: 'bold', fontSize: '17px', color: '#ffffff',
      }));
      modal.add(this.add.text(x + 62, y + 26, item.description, {
        fontFamily: 'Arial', fontSize: '15px', color: '#c1cee1',
        wordWrap: { width: 300 }, lineSpacing: 3,
      }));
    });
    modal.add(this.add.text(GAME_W / 2, top + 673, 'Bonusurile permanente se păstrează la respawn, până la nivelul următor.', {
      fontFamily: 'Arial', fontSize: '15px', color: '#80e9dc',
    }).setOrigin(0.5));
    const closeButton = this.add.text(GAME_W / 2, top + 709, '[ ÎNCHIDE ]', {
      fontFamily: 'Arial Black, Arial', fontSize: '20px', color: '#99ff77',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    modal.add(closeButton);
    const close = () => {
      this.input.keyboard?.off('keydown-ESC', close);
      this.events.off(Phaser.Scenes.Events.SHUTDOWN, close);
      modal.destroy(); this.helpPanel = null; this.menuElement.hidden = false;
    };
    overlay.on('pointerdown', close); closeButton.on('pointerdown', close);
    this.input.keyboard?.once('keydown-ESC', close);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, close);
  }
}
